import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ClipboardList, 
  AlertTriangle,
  Wrench,
  FileText,
  Building,
  Plus,
  ArrowRight,
  Settings
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FormsPage = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [formTypes, setFormTypes] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, typesRes] = await Promise.all([
        axios.get(`${API}/forms/templates`, { withCredentials: true }),
        axios.get(`${API}/forms/types`, { withCredentials: true })
      ]);
      setTemplates(templatesRes.data);
      setFormTypes(typesRes.data);
      
      // Try to get stats (admin only)
      if (user?.role === 'super_admin' || user?.role === 'company_admin') {
        try {
          const statsRes = await axios.get(`${API}/forms/stats`, { withCredentials: true });
          setStats(statsRes.data);
        } catch (e) {
          // Stats not available for this user
        }
      }
    } catch (error) {
      console.error("Error fetching forms data:", error);
      toast.error("Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  const getFormIcon = (formType) => {
    switch (formType) {
      case 'incident': return <AlertTriangle className="w-6 h-6" />;
      case 'equipment_inspection': return <Wrench className="w-6 h-6" />;
      case 'job_hazard_analysis': return <FileText className="w-6 h-6" />;
      case 'jobsite_inspection': return <Building className="w-6 h-6" />;
      default: return <ClipboardList className="w-6 h-6" />;
    }
  };

  const getFormColor = (formType) => {
    switch (formType) {
      case 'incident': return 'bg-rose-100 text-rose-600';
      case 'equipment_inspection': return 'bg-blue-100 text-blue-600';
      case 'job_hazard_analysis': return 'bg-amber-100 text-amber-600';
      case 'jobsite_inspection': return 'bg-emerald-100 text-emerald-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="forms-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              Safety Forms
            </h1>
            <p className="text-muted-foreground mt-1">
              Submit incident reports, inspections, and safety documentation.
            </p>
          </div>
          {(user?.role === 'super_admin' || user?.role === 'company_admin') && (
            <div className="flex gap-3">
              <Link to="/forms/builder">
                <Button data-testid="create-form-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Custom Form
                </Button>
              </Link>
              <Link to="/forms/submissions">
                <Button variant="outline" data-testid="view-submissions-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  View Submissions
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Stats for admins */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Total Submissions</p>
                <p className="font-heading text-2xl font-bold text-primary">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Incidents</p>
                <p className="font-heading text-2xl font-bold text-rose-600">{stats.by_type?.incident || 0}</p>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="font-heading text-2xl font-bold text-amber-600">{stats.by_status?.submitted || 0}</p>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Closed</p>
                <p className="font-heading text-2xl font-bold text-emerald-600">{stats.by_status?.closed || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Form Types */}
        <div>
          <h2 className="font-heading text-lg font-semibold text-primary mb-4">Available Forms</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(formTypes).map(([type, config]) => {
              // Find template for this type
              const template = templates.find(t => t.form_type === type);
              
              return (
                <Card key={type} className="card-hover" data-testid={`form-type-${type}`}>
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-xl ${getFormColor(type)} flex items-center justify-center mb-4`}>
                      {getFormIcon(type)}
                    </div>
                    <h3 className="font-heading font-semibold text-primary mb-2">
                      {config.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {config.default_fields?.length || 0} fields
                    </p>
                    {template ? (
                      <Link to={`/forms/submit/${template.template_id}`}>
                        <Button className="w-full" data-testid={`submit-form-${type}`}>
                          <Plus className="w-4 h-4 mr-2" />
                          New Submission
                        </Button>
                      </Link>
                    ) : (
                      <Button className="w-full" disabled>
                        Template Not Available
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Recent Submissions (for admins) */}
        {stats?.recent && stats.recent.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Recent Submissions</CardTitle>
              <Link to="/forms/submissions">
                <Button variant="ghost" size="sm">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent.slice(0, 5).map((sub) => (
                  <div key={sub.submission_id} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${getFormColor(sub.form_type)} flex items-center justify-center`}>
                        {getFormIcon(sub.form_type)}
                      </div>
                      <div>
                        <p className="font-medium text-primary">{sub.title}</p>
                        <p className="text-sm text-muted-foreground">
                          by {sub.submitted_by_name} • {new Date(sub.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {sub.severity && (
                        <Badge className={`badge-${sub.severity === 'critical' || sub.severity === 'high' ? 'danger' : sub.severity === 'medium' ? 'warning' : 'neutral'}`}>
                          {sub.severity}
                        </Badge>
                      )}
                      <Badge className={`badge-${sub.status === 'closed' ? 'success' : sub.status === 'reviewed' ? 'warning' : 'neutral'}`}>
                        {sub.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FormsPage;
